import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ScheduleCalendar from "@/components/ScheduleCalendar";
import CalendarMenu from "@/components/CalendarMenu";
import AttendanceSheetDialog from "@/components/AttendanceSheetDialog";
import ConflictDialog from "@/components/ConflictDialog";
import { useAuth } from "@/context/AuthContext";
import { todayInput } from "@/lib/date";
import { useFeatureFlags, FeatureDisabled } from "@/lib/featureFlags";
import ScheduleTabs, { useTabParam } from "@/components/ScheduleTabs";
import MemberVolunteers from "@/pages/member/Volunteers";
import MemberAttendanceHistory from "@/pages/member/AttendanceHistory";

export default function MemberSchedule() {
  const { schedule: scheduleOn, attendance: attendanceOn, conflicts: conflictsOn, volunteers: volunteersOn } = useFeatureFlags();
  const tabs = [{ key: "calendar", label: "Calendar" }, attendanceOn && { key: "attendance", label: "Attendance" },
    volunteersOn && { key: "volunteers", label: "Volunteers" }].filter(Boolean);
  const [tab, setTab] = useTabParam(tabs.map((t) => t.key));
  const { user } = useAuth();
  const [conflicts, setConflicts] = useState({});
  const [conflictEvent, setConflictEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [canTake, setCanTake] = useState(false);
  const [attendEvent, setAttendEvent] = useState(null);
  const [marks, setMarks] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/member/schedule");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Latest conflict per event (most recent submission wins).
  const loadConflicts = useCallback(async () => {
    if (!conflictsOn) { setConflicts({}); return; }
    const { data } = await api.get("/conflicts/my");
    const m = {};
    [...(data.items || [])].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")).forEach((c) => { m[c.event_id] = c; });
    setConflicts(m);
  }, [conflictsOn]);
  useEffect(() => { loadConflicts().catch(() => {}); }, [loadConflicts]);

  const myGroups = user?.subgroup_ids || [];
  const conflictInfo = conflictsOn ? (it) => {
    const conflict = conflicts[it.id];
    const invited = !(it.subgroup_ids || []).length || it.subgroup_ids.some((g) => myGroups.includes(g));
    const upcoming = (it.event_date || "").slice(0, 10) >= todayInput();
    return { conflict, canSubmit: invited && upcoming && (!conflict || conflict.status === "declined") };
  } : undefined;

  useEffect(() => {
    if (!attendanceOn) { setCanTake(false); setMarks({}); return; }
    api.get("/attendance/can-take").then(({ data }) => setCanTake(!!data.can_take)).catch(() => setCanTake(false));
    api.get("/attendance/my").then(({ data }) => setMarks(Object.fromEntries((data.records || []).map((r) => [r.event_id, r.status])))).catch(() => setMarks({}));
  }, [attendanceOn]);

  if (!scheduleOn) return <FeatureDisabled name="Daily Schedule" />;

  return (
    <div>
      <PageHeader title="Daily Schedule" subtitle="Team events and your scheduled conferences" actions={<CalendarMenu />} />
      <ScheduleTabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === "attendance" && <MemberAttendanceHistory />}
      {tab === "volunteers" && <MemberVolunteers />}
      {tab === "calendar" && <ScheduleCalendar items={items} showAttendance={canTake} onAttendance={(it) => setAttendEvent(it)} conflictInfo={conflictInfo} onConflict={setConflictEvent} onVolunteer={volunteersOn ? () => setTab("volunteers") : undefined} attendanceFor={(id) => marks[id]} />}
      {conflictEvent && <ConflictDialog event={conflictEvent} open onClose={() => setConflictEvent(null)} onSaved={loadConflicts} />}
      {attendEvent && <AttendanceSheetDialog event={attendEvent} open onClose={() => setAttendEvent(null)} onSaved={load} />}
    </div>
  );
}
